import { orderBy } from "lodash";

import { PortfolioFamiliesResponse } from "@fartherfinance/frontend/api/Investment/request/getPortfolioFamilies";
import { PortfolioFamiliesByIdResponse } from "@fartherfinance/frontend/api/Investment/request/getPortfolioFamiliesById";
import { PortfolioPlanByIdResponse } from "@fartherfinance/frontend/api/Investment/request/getPortfolioPlanById";
import { PortfolioPlanResponse } from "@fartherfinance/frontend/api/Investment/types";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import { ModelPortfolioTypeOptions } from "./constants";
import { ListItemOption } from "./types";

type TransformPortfolioArgs =
  | {
      name: ModelPortfolioTypeOptions.FARTHER_LIST;
      data: PortfolioFamiliesResponse["data"];
    }
  | {
      name: ModelPortfolioTypeOptions.FARTHER_MODEL_MARKET_PLACE;
      data: PortfolioFamiliesResponse["data"];
    }
  | {
      name: ModelPortfolioTypeOptions.FARTHER_FAMILY_BY_ID;
      data: PortfolioFamiliesByIdResponse["data"];
    }
  | {
      name: ModelPortfolioTypeOptions.SHARED_BY_ADVISOR;
      data: PortfolioPlanResponse["data"];
    }
  | {
      name: ModelPortfolioTypeOptions.CUSTOM_PORTFOLIOS;
      data: PortfolioPlanResponse["data"];
    }
  | {
      name: ModelPortfolioTypeOptions.PORTFOLIO_PLAN_BY_ID;
      data: PortfolioPlanByIdResponse["data"];
    };

export function transformPortfolioResponseData({
  name,
  data,
}: TransformPortfolioArgs): ListItemOption[] {
  if (name === ModelPortfolioTypeOptions.FARTHER_LIST) {
    const fartherAssetManagementData = data
      .filter((pf) => pf.name === "FAM")
      .map((pf) => ({
        id: pf.portfolioFamilyId,
        title: "Farther Asset Management",
        type: ModelPortfolioTypeOptions.FARTHER_FAMILY_BY_ID,
      }));

    return fartherAssetManagementData;
  }
  if (name === ModelPortfolioTypeOptions.FARTHER_MODEL_MARKET_PLACE) {
    const marketPlacePlans = data.filter((pf) => pf.name !== "FAM");

    const marketPlacePlanItems = marketPlacePlans.map((pf) => ({
      id: pf.portfolioFamilyId,
      title: pf.name,
      type: ModelPortfolioTypeOptions.FARTHER_FAMILY_BY_ID,
    }));

    const sortedMarketplaceItems = orderBy(
      marketPlacePlanItems,
      ["title"],
      ["asc"]
    );

    return sortedMarketplaceItems;
  }

  if (name === ModelPortfolioTypeOptions.FARTHER_FAMILY_BY_ID) {
    if (data.children?.length > 0) {
      const mapped = data.children
        .filter(
          (child) => child.type === "Family" || child.type === "Portfolio-Plan"
        )
        .map((child) => {
          if (child.type === "Family") {
            return {
              id: child.portfolioFamily.portfolioFamilyId,
              title: child.portfolioFamily.name,
              type: ModelPortfolioTypeOptions.FARTHER_FAMILY_BY_ID,
            };
          }

          if (child.type === "Portfolio-Plan") {
            return {
              id: child.portfolioPlan.portfolioPlanId,
              title: child.portfolioPlan.name,
              type: ModelPortfolioTypeOptions.PORTFOLIO_PLAN_BY_ID,
            };
          }

          return null;
        })
        .filter((item) => item !== null);

      return orderBy(mapped, ["title"], ["asc"]);
    }
    return [];
  }

  if (name === ModelPortfolioTypeOptions.PORTFOLIO_PLAN_BY_ID) {
    return [
      {
        id: data.plan.portfolioPlanId,
        title: data.plan.name,
        type: ModelPortfolioTypeOptions.PORTFOLIO_PLAN_BY_ID,
        securityEntries: data.plan.entries,
      },
    ];
  }

  if (name === ModelPortfolioTypeOptions.SHARED_BY_ADVISOR) {
    return orderBy(
      data.map(({ plan }) => ({
        title: plan.name,
        id: plan.portfolioPlanId as PortfolioPlanId,
        type: ModelPortfolioTypeOptions.SHARED_BY_ADVISOR,
      })),
      ["title"],
      ["asc"]
    );
  }

  if (name === ModelPortfolioTypeOptions.CUSTOM_PORTFOLIOS) {
    return orderBy(
      data.map(({ plan }) => ({
        title: plan.name,
        id: plan.portfolioPlanId as PortfolioPlanId,
        type: ModelPortfolioTypeOptions.CUSTOM_PORTFOLIOS,
      })),
      ["title"],
      ["asc"]
    );
  }

  return [];
}

export const getTransformArgs = (
  name: ModelPortfolioTypeOptions,
  data: unknown
): TransformPortfolioArgs | null => {
  switch (name) {
    case ModelPortfolioTypeOptions.FARTHER_LIST:
    case ModelPortfolioTypeOptions.FARTHER_MODEL_MARKET_PLACE:
      return {
        name,
        data: data as PortfolioFamiliesResponse["data"],
      };
    case ModelPortfolioTypeOptions.FARTHER_FAMILY_BY_ID:
      return {
        name,
        data: data as PortfolioFamiliesByIdResponse["data"],
      };
    case ModelPortfolioTypeOptions.SHARED_BY_ADVISOR:
    case ModelPortfolioTypeOptions.CUSTOM_PORTFOLIOS:
      return {
        name,
        data: data as PortfolioPlanResponse["data"],
      };
    case ModelPortfolioTypeOptions.PORTFOLIO_PLAN_BY_ID:
      return {
        name,
        data: data as PortfolioPlanByIdResponse["data"],
      };
    default:
      return null;
  }
};
